import { Box, FormHelperText, Grow, Paper, Stack, styled, Switch, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react'

import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import GppGoodIcon from '@mui/icons-material/GppGood';
import CustomTooltip from 'Common/CustomTooltip';
import { Info } from '@mui/icons-material';
import { currencyFormatter } from 'Utils/currencyFormatter';

const PaperContainer = styled("div")({
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    flex: 1,
    height: '100%',
    minHeight: 0,
    boxSizing: 'border-box',
});

// Dark theme color palette (from HTML example)
const colorBg = '#121826'
const colorText = '#e9edf5'
const colorMuted = '#b6c0d6'
const colorAccent = '#4f7cff'
const colorAccent2 = '#22c55e'
const colorBorder = '#1f2937'
const colorDisabled = '#6b7280'

const PaperStyled = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'selected',
})(({ selected }) => ({
    background: selected ? `linear-gradient(135deg, ${colorBg}, #1a2332)` : colorBg,
    border: `2px solid ${selected ? colorAccent2 : colorBorder}`,
    borderRadius: '14px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '820px',
    width: '100%',
    boxSizing: 'border-box',
    transition: 'all 0.3s ease',
    marginBottom: '12px',
    boxShadow: selected
        ? `0 0 0 3px ${colorAccent2}50, 0 12px 35px rgba(34, 197, 94, 0.4), 0 0 60px rgba(34, 197, 94, 0.2)`
        : '0 2px 8px rgba(0,0,0,.2)',
    transform: selected ? 'scale(1.02) translateY(-4px)' : 'scale(1)',
    opacity: selected ? 1 : 0.85,
    '&:hover': {
        transform: selected ? 'scale(1.02) translateY(-4px)' : 'translateY(-2px)',
        boxShadow: selected
            ? `0 0 0 3px ${colorAccent2}70, 0 15px 40px rgba(34, 197, 94, 0.5), 0 0 70px rgba(34, 197, 94, 0.3)`
            : '0 10px 25px rgba(0,0,0,.35)',
        opacity: 1,
    }
}));

const CardHeader = styled('div')(({ selected }) => ({
    padding: '1.2rem 1rem',
    borderBottom: `1px solid ${colorBorder}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '.8rem',
    background: selected ? `linear-gradient(135deg, ${colorAccent2}15, transparent)` : 'transparent',
}));

const Badge = styled('span')({
    fontSize: '.8rem',
    color: colorAccent,
    border: `1px dashed ${colorAccent}`,
    padding: '.2rem .5rem',
    borderRadius: '.5rem',
    whiteSpace: 'nowrap',
});

const PriceSection = styled('div')(({ selected }) => ({
    padding: '1rem',
    display: 'flex',
    alignItems: 'baseline',
    gap: '.6rem',
    borderBottom: `1px dashed ${colorBorder}`,
    background: selected ? `${colorAccent2}08` : 'transparent',
}));

const AntSwitch = styled(Switch)({
    width: 60,
    height: 32,
    padding: 0,
    display: 'flex',
    background: colorBg,
    border: `1px solid ${colorBorder}`,
    borderRadius: '999px',
    '& .MuiSwitch-switchBase': {
        padding: 2,
        '&.Mui-checked': {
            transform: 'translateX(28px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: '#e9edf5',
                border: 0,
            },
            '& .MuiSwitch-thumb': {
                background: colorAccent,
            },
        },
    },
    '& .MuiSwitch-thumb': {
        boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.5)',
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: colorMuted,
        transition: 'all .25s ease',
    },
    '& .MuiSwitch-track': {
        borderRadius: '999px',
        opacity: 1,
        backgroundColor: 'transparent',
        boxSizing: 'border-box',
    },
});

const TickIcon = styled(DoneIcon)({});
const CrossIcon = styled(CloseIcon)({});
const ShieldIcon = styled(GppGoodIcon)({});

const PricingCard = ({ selected, OFFER, handleSelect, index, billAnnually, onToggleChange, roleType }) => {
    const [annualPrice, setAnnualPrice] = useState(OFFER.annually);

    const handleBillTypeChange = (e) => {
        // Notify parent component of toggle change
        onToggleChange(OFFER.id, e.target.checked);
    }

    // Determine trial period based on role type
    const trialPeriod = roleType === 'advisor' ? 90 : 30;

    const getElementIcon = (element) => {
        if (element.icon === "check" && element.info !== "0")
            return <TickIcon />
        else if (element.icon === "shield" && element.info !== "0")
            return <ShieldIcon />
        else return <CrossIcon style={{ color: colorDisabled }} />
    }

    useEffect(() => {
        if (billAnnually) {
            setAnnualPrice(OFFER.annually);
        } else {
            const temp = OFFER.monthly * 12;
            setAnnualPrice(temp.toFixed(2));
        }
    }, [billAnnually, OFFER.annually, OFFER.monthly]);

    return (
        <Grow in timeout={600 + index * 300}>
            <PaperContainer
                onClick={(e) => {
                    // Prevent card selection when clicking on the switch, input, or related elements
                    if (e.target.closest('.MuiSwitch-root') ||
                        e.target.closest('input[type="checkbox"]') ||
                        e.target.closest('.MuiSwitch-switchBase')) {
                        return;
                    }
                    e.stopPropagation();
                    handleSelect(OFFER.id)
                }}>
                <PaperStyled selected={selected === OFFER.id}>
                    {/* Card Header */}
                    <CardHeader selected={selected === OFFER.id}>
                        <Typography sx={{
                            margin: 0,
                            fontSize: '1.15rem',
                            fontWeight: 600,
                            letterSpacing: '.2px',
                            color: colorText,
                        }}>
                            {OFFER.name}
                        </Typography>
                        {OFFER.badgeText && (
                            <Badge>{OFFER.badgeText}</Badge>
                        )}
                    </CardHeader>

                    {/* Price Section */}
                    {OFFER.name !== "Trial" ? (
                        <>
                            <PriceSection selected={selected === OFFER.id}>
                                <Typography sx={{
                                    fontSize: '1.6rem',
                                    fontWeight: 700,
                                    color: colorText,
                                }}>
                                    {`${OFFER.currency} ${currencyFormatter(OFFER.monthly)}`}
                                </Typography>
                                <Typography sx={{
                                    color: colorMuted,
                                    fontSize: '.95rem',
                                }}>
                                    / month
                                </Typography>
                                <Typography sx={{
                                    marginLeft: 'auto',
                                    fontSize: '.85rem',
                                    color: colorAccent2,
                                    background: `color-mix(in oklab, ${colorAccent2} 15%, transparent)`,
                                    border: `1px solid color-mix(in oklab, ${colorAccent2} 45%, transparent)`,
                                    padding: '.25rem .4rem',
                                    borderRadius: '.4rem',
                                }}>
                                    {billAnnually
                                        ? `${OFFER.currency} ${currencyFormatter(annualPrice)} / year · 1 month free`
                                        : `${OFFER.currency} ${currencyFormatter(annualPrice)} / year`
                                    }
                                </Typography>
                            </PriceSection>

                            {/* Billing Toggle */}
                            <Box sx={{
                                display: 'flex',
                                gap: '.75rem',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '1rem',
                                borderBottom: `1px dashed ${colorBorder}`,
                                color: colorMuted,
                                fontSize: '.95rem',
                            }}>
                                <Typography sx={{ fontSize: '.95rem', color: colorMuted }}>
                                    Monthly
                                </Typography>
                                <AntSwitch
                                    checked={billAnnually}
                                    inputProps={{ 'aria-label': 'billing toggle' }}
                                    onChange={(e) => handleBillTypeChange(e)}
                                />
                                <Typography sx={{ fontSize: '.95rem', color: colorMuted }}>
                                    Annually
                                </Typography>
                            </Box>
                        </>
                    ) : (
                        <Box sx={{
                            padding: '1.5rem 1rem',
                            textAlign: 'center',
                            borderBottom: `1px dashed ${colorBorder}`,
                        }}>
                            <Typography sx={{
                                fontSize: '1.8rem',
                                fontWeight: 700,
                                color: colorAccent2,
                                marginBottom: '.5rem',
                            }}>
                                FREE
                            </Typography>
                            <Typography sx={{
                                color: colorMuted,
                                fontSize: '.9rem',
                            }}>
                                {trialPeriod} day trial period
                            </Typography>
                        </Box>
                    )}
                    {/* Features List */}
                    <Box sx={{
                        listStyle: 'none',
                        margin: 0,
                        padding: '.25rem 1rem 1rem',
                        display: 'grid',
                        gap: '.5rem',
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: `linear-gradient(to bottom, ${colorBorder}, ${colorBg})`,
                            borderRadius: '10px',
                            border: `1px solid ${colorBorder}`,
                            boxShadow: 'inset 0 0 6px rgba(0,0,0,.3)',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: `linear-gradient(180deg, ${colorAccent}, #3f6cf8)`,
                            borderRadius: '10px',
                            border: `2px solid ${colorAccent}`,
                            boxShadow: `0 0 6px ${colorAccent}80`,
                            '&:hover': {
                                border: `2px solid ${colorBg}`,
                                background: `linear-gradient(180deg, ${colorAccent2}, #1ea34d)`,
                                boxShadow: `0 0 8px ${colorAccent}`,
                            }
                        }
                    }}>
                        {OFFER?.offers?.map((element, index) => {
                            // Show tooltip only if description exists and is not empty
                            const showTooltip = element.description && element.description.trim().length > 0;

                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'start',
                                        gap: '.6rem',
                                        borderBottom: index === OFFER.offers.length - 1 ? 0 : `1px dashed ${colorBorder}`,
                                        padding: '.65rem 0',
                                    }}
                                >
                                    <Box sx={{
                                        minWidth: '18px',
                                        height: '18px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: (element.icon === false || element.info === "0") ? colorDisabled : colorAccent2,
                                        '& svg': { fontSize: '18px' }
                                    }}>
                                        {getElementIcon(element)}
                                    </Box>
                                    <Box sx={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '.4rem',
                                    }}>
                                        <Typography sx={{
                                            color: (element.icon === false || element.info === "0") ? colorDisabled : colorMuted,
                                            fontSize: '.92rem',
                                            textAlign: 'left',
                                        }}>
                                            {element.desc}
                                        </Typography>
                                        {showTooltip && (
                                            <CustomTooltip title={element.description}>
                                                <Typography sx={{
                                                    fontSize: '14px',
                                                    color: colorAccent,
                                                    cursor: 'help',
                                                    flexShrink: 0,
                                                    fontWeight: 600,
                                                    userSelect: 'none',
                                                }}>
                                                    ?
                                                </Typography>
                                            </CustomTooltip>
                                        )}
                                    </Box>
                                    <Box sx={{ minWidth: '110px', textAlign: 'right' }}>
                                        {element.info === "Contact Sales" || element.info === "Monthly/Annual" ? (
                                            <Typography sx={{
                                                fontWeight: 600,
                                                fontSize: '.95rem',
                                                color: colorAccent,
                                            }}>
                                                {element.info}
                                            </Typography>
                                        ) : (
                                            <Typography sx={{
                                                fontWeight: 600,
                                                fontSize: '.95rem',
                                                color: (element.icon === false || element.info === "0") ? colorDisabled : colorText,
                                            }}>
                                                {!Number.isNaN(Number(element.info)) ? currencyFormatter(element.info) : element.info}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                    {/* CTA Section */}
                    {OFFER.name !== "Trial" && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '.6rem',
                            padding: '1rem',
                            borderTop: `1px solid ${colorBorder}`,
                            marginTop: 'auto',
                        }}>
                            {OFFER.name !== "Trial" && (
                                <Box
                                    component="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelect(OFFER.id);
                                    }}
                                    sx={{
                                        textDecoration: 'none',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '.5rem',
                                        padding: '.65rem .9rem',
                                        borderRadius: '.6rem',
                                        fontWeight: 600,
                                        fontSize: '.95rem',
                                        border: selected === OFFER.id ? 'none' : `1px solid ${colorBorder}`,
                                        color: 'white',
                                        background: selected === OFFER.id
                                            ? `linear-gradient(180deg, ${colorAccent2}, #1ea34d)`
                                            : `linear-gradient(180deg, ${colorAccent}, #3f6cf8)`,
                                        cursor: 'pointer',
                                        transition: 'transform .05s ease, background .2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-1px)',
                                        },
                                        '&:active': {
                                            transform: 'translateY(0)',
                                        }
                                    }}
                                >
                                    {selected === OFFER.id ? "✓ Selected" : "Subscribe"}
                                </Box>
                            )}
                            {OFFER.infoText?.map((element, index) => (
                                <FormHelperText
                                    key={index}
                                    sx={{
                                        color: colorMuted,
                                        fontSize: '.85rem',
                                        textAlign: 'center',
                                        lineHeight: 1.4,
                                        margin: 0,
                                    }}
                                >
                                    {element}
                                </FormHelperText>
                            ))}
                        </Box>
                    )}
                </PaperStyled>
            </PaperContainer>
        </Grow>
    )
}

export default PricingCard